<template>
  <div class="pa-4 h-100">
    <v-container grid-list-xl fluid>
      <v-card class="mb-4">
        <v-card-title>Udemy Access</v-card-title>
        <v-card-subtitle>
          Shared corporate Udemy login — verification code appears here automatically.
        </v-card-subtitle>
        <v-card-text>
          <v-alert type="info" dense outlined class="mb-4">
            <ol class="mb-0 pl-4">
              <li>Go to <a href="https://www.udemy.com/" target="_blank" rel="noopener noreferrer">udemy.com</a></li>
              <li>Enter login: <strong>{{ mailboxAddress }}</strong></li>
              <li>When Udemy asks for a code — copy it from this page (valid 15 minutes)</li>
            </ol>
          </v-alert>

          <div class="text-center py-6">
            <div v-if="initialLoading" class="py-8">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <p class="mt-4 grey--text">Loading...</p>
            </div>

            <div v-else-if="code">
              <div class="udemy-code-display">{{ code }}</div>
              <p class="mt-2 grey--text">Expires in {{ formattedCountdown }}</p>
              <v-btn color="primary" class="mt-3" @click="copyCode">
                <v-icon left>mdi-content-copy</v-icon>
                Copy code
              </v-btn>
            </div>

            <v-alert v-else-if="mailboxNotConnected" type="warning" dense outlined>
              Udemy mailbox is not connected yet. Contact HR to complete setup.
            </v-alert>

            <v-alert v-else type="info" dense outlined>
              No active code right now. Start Udemy login with <strong>{{ mailboxAddress }}</strong> — the code will appear here within a few seconds.
            </v-alert>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-if="canConnectMailbox">
        <v-card-title class="subtitle-1">Admin: Connect mailbox</v-card-title>
        <v-card-subtitle>
          One-time setup — sign in with <strong>{{ mailboxAddress }}</strong> to allow DCM to read OTP emails.
        </v-card-subtitle>
        <v-card-text>
          <v-btn
            v-if="!mailboxConnected"
            color="primary"
            :loading="isConnecting"
            :disabled="isConnecting"
            @click="connectMailbox"
          >
            Connect Udemy mailbox
          </v-btn>
          <v-alert v-else type="success" dense>
            Connected to {{ mailboxAddress }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
const POLL_INTERVAL_MS = 10000

export default {
  name: 'UdemyAccess',
  data () {
    return {
      code: null,
      expiresInSeconds: 0,
      mailboxAddress: 'seminars@devcom.com',
      initialLoading: true,
      isConnecting: false,
      mailboxNotConnected: false,
      mailboxConnected: false,
      pollTimer: null,
      countdownTimer: null,
      pollingPaused: false
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    canConnectMailbox () {
      const roles = this.$store.getters.getEmployeeRoles
      return roles.hr.val || roles.financier.val
    },
    formattedCountdown () {
      if (this.expiresInSeconds <= 0) {
        return 'expired'
      }
      const minutes = Math.floor(this.expiresInSeconds / 60)
      const seconds = this.expiresInSeconds % 60
      return `${minutes}:${String(seconds).padStart(2, '0')}`
    }
  },
  created () {
    this.fetchLatestCode({ isInitial: true })
    this.pollTimer = setInterval(() => this.fetchLatestCode(), POLL_INTERVAL_MS)
    this.countdownTimer = setInterval(this.tickCountdown, 1000)
  },
  beforeDestroy () {
    clearInterval(this.pollTimer)
    clearInterval(this.countdownTimer)
  },
  methods: {
    authHeaders () {
      return { Authorization: 'Bearer ' + this.user.token }
    },
    applyMailboxStatus (data) {
      if (data.mailboxAddress) {
        this.mailboxAddress = data.mailboxAddress
      }
      if (data.mailboxConnected === false) {
        this.mailboxNotConnected = true
        this.mailboxConnected = false
        return
      }
      if (data.mailboxConnected || data.code) {
        this.mailboxNotConnected = false
        this.mailboxConnected = true
      }
    },
    applyCodeData (data) {
      if (!data.code) {
        if (!this.code) {
          this.expiresInSeconds = 0
        }
        return
      }
      const codeChanged = this.code !== data.code
      this.code = data.code
      if (codeChanged || this.expiresInSeconds <= 0) {
        this.expiresInSeconds = data.expiresInSeconds
      }
    },
    async fetchLatestCode ({ isInitial = false } = {}) {
      if (!this.user || !this.user.token || this.pollingPaused) {
        return
      }
      try {
        const { data } = await this.axios.get('/api/udemy/latest-code', {
          headers: this.authHeaders()
        })
        this.applyMailboxStatus(data)
        this.applyCodeData(data)
      } catch (error) {
        if (error.response && error.response.status === 503) {
          this.mailboxNotConnected = true
          this.mailboxConnected = false
          this.code = null
        } else if (error.response && error.response.status !== 429) {
          console.error('[Udemy Access] Failed to fetch code:', error)
        }
      } finally {
        if (isInitial) {
          this.initialLoading = false
        }
      }
    },
    tickCountdown () {
      if (this.expiresInSeconds > 0) {
        this.expiresInSeconds -= 1
      }
      if (this.expiresInSeconds <= 0 && this.code) {
        this.code = null
      }
    },
    copyCode () {
      if (!this.code) {
        return
      }
      navigator.clipboard.writeText(this.code)
        .then(() => this.$store.dispatch('setSuccess', { successMsg: 'Code copied to clipboard' }))
        .catch(() => this.$store.dispatch('setError', { errorMsg: 'Failed to copy code' }))
    },
    connectMailbox () {
      this.pollingPaused = true
      this.$client((code) => {
        this.pollingPaused = false
        if (code) {
          this.submitMailboxConnect(code)
        }
      }).requestCode()
    },
    submitMailboxConnect (code) {
      this.isConnecting = true
      this.axios.post(
        '/api/udemy/connect-mailbox',
        { code, redirect_uri: window.location.origin },
        { headers: this.authHeaders() }
      )
        .then((response) => {
          this.mailboxNotConnected = false
          this.mailboxConnected = true
          if (response.data.mailboxAddress) {
            this.mailboxAddress = response.data.mailboxAddress
          }
          this.$store.dispatch('setSuccess', { successMsg: 'Udemy mailbox connected' })
          this.fetchLatestCode()
        })
        .catch((error) => {
          const detail = typeof error.response?.data === 'string'
            ? error.response.data
            : error.message
          this.$store.dispatch('setError', {
            errorMsg: 'Failed to connect mailbox: ' + detail
          })
        })
        .finally(() => {
          this.isConnecting = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.udemy-code-display {
  font-size: 3rem;
  font-weight: 700;
  letter-spacing: 0.5rem;
  font-family: monospace;
  color: #1976d2;
}
</style>
